import { Injectable } from '@angular/core';
import { Enums } from '@cyborg-monorepo/hunter/common';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { deepEqual } from '../../../helpers/utils';
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../tracking/tracking.service';
import {
  ResolvedSearchResultData,
  SearchDataService,
} from './search-data.service';

export interface Tag {
  key: string;
  doc_count: number;
}

export interface TagType {
  type: string;
  tags?: Tag[];
}

export interface TagDictionary {
  [key: string]: Tag[];
}

interface DenormalizedFilter {
  type: string;
  filter: string;
}

export interface SearchFilters {
  [key: string]: string[];
}

/**
 * All user-settable search options
 */
export interface SearchStateInputs {
  filters: SearchFilters;
  touched: boolean;
  page: number;
  size: number;
  sort: Enums.CyborgSortEnum;
  term: Array<string>;
  lastUpdated?: number;
  compatible: boolean;
  library: Array<Enums.CyborgIndexEnum>;
}

/**
 * SearchState extended by any computed values based on user-provided inputs, but not provided directly
 */
export interface SearchStateExtended extends SearchStateInputs {
  filtersDenormalized: DenormalizedFilter[];
}

export interface SearchResultFacets {
  allTags: TagDictionary;
  tags: TagType[];
  mitreTags: TagType[];
  killChains: TagType[];
  tools: TagType[];
  platformTypes: TagType[];
  dataSources: TagType[];
  goals: TagType[];
  dependencies: TagType[];
}

export const DEFAULT_SEARCH_STATE: SearchStateInputs = {
  filters: {},
  touched: false,
  page: 0,
  size: 10,
  sort: Enums.CyborgSortEnum.RELEVANCE,
  compatible: false,
  term: [],
  library: [
    Enums.CyborgIndexEnum.CYBORG_THREAT_PROFILES,
    Enums.CyborgIndexEnum.CYBORG_USE_CASES,
    Enums.CyborgIndexEnum.CYBORG_COLLECTIONS,
  ],
};

export interface SearchResults {
  state: SearchStateExtended;
  data: ResolvedSearchResultData;
  facets: SearchResultFacets;
}

@Injectable()
export class SearchStateService {
  /**
   * The complete current state of the search queryable filters and values, extended with any calculated values based on other state values, updated only when full state has changed
   *
   * Private internal state observable hooked back to public BehaviorSubject for .value compat throughout
   */
  private internalState: Subject<SearchStateInputs> =
    new Subject<SearchStateInputs>();
  public state: BehaviorSubject<SearchStateExtended> = new BehaviorSubject({
    ...DEFAULT_SEARCH_STATE,
    filtersDenormalized: [],
  });

  /**
   * The search results based on the above state, piped alongside a loading indicator subject set to true when new results are being retrieved and false when up to date
   */
  public resultsLoading: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  public resultsError: ReplaySubject<Error> = new ReplaySubject<Error>(1);
  public results: Observable<SearchResults> = this.state.pipe(
    tap(() => {
      this.resultsLoading.next(true);
      this.resultsError.next(null);
    }),
    switchMap((state) => {
      return this.searchDataService.fetchResults(state).pipe(
        catchError((e: Error) => {
          console.error(e);
          this.resultsError.next(e);
          return EMPTY;
        }),
      );
    }),
    map(({ state, data, tags }) => {
      return {
        state,
        data,
        facets: this.tagsToFacets(tags),
      };
    }),
    tap(() => this.resultsLoading.next(false)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private searchDataService: SearchDataService,
    private trackingService: TrackingService,
  ) {
    // Connect internal state distinct updates into external final computed state
    this.internalState
      .pipe(map(this.generateExtendedState), distinctUntilChanged(deepEqual))
      .subscribe(this.state);

    this.state
      .pipe(
        // 15 seconds to account for building searches incrementally
        debounceTime(15000),
        distinctUntilChanged(),
        filter(
          (state) =>
            !deepEqual(state, {
              ...DEFAULT_SEARCH_STATE,
              filtersDenormalized: [],
            }),
        ),
      )
      .subscribe((state) => {
        this.trackingService.trackEvent(TrackingEventEnum.RESEARCH_SEARCH, {
          search_state: state,
        });
      });
  }

  /**
   * Sets the entire state to the passed object, defaulting any unprovided values back to app defaults.
   * Will only update external state if there are changes between the passed state and the current search state.
   *
   * @param state
   */
  public setState(state: Partial<SearchStateInputs>): void {
    this.updateState({ ...DEFAULT_SEARCH_STATE, ...state });
  }

  /**
   * Add one or more filters to the current search query.
   *
   * @param filterType The name of the filter, ie: MitreTactic names
   * @param filterValue The value of the filter, ie: Defense Evasion
   */
  public addFilter(
    filterType: string,
    filterValue: string | Array<string>,
  ): void {
    const filterValueArray = Array.isArray(filterValue)
      ? filterValue
      : [filterValue];

    const state = this.state.value;
    const filters: SearchFilters = { ...state.filters };
    const thisFilter: string[] = filters[filterType] || [];

    filters[filterType] = [...thisFilter, ...filterValueArray];

    this.updateState({
      filters,
      page: 0,
    });
  }

  /**
   * Delete a filter off the current search query.
   *
   * @param filterType The name of the filter, ie: MitreTactic names
   * @param filterValue The value of the filter, ie: Defense Evasion
   */
  public deleteFilter(filterType: string, filterValue: string): void {
    const state = this.state.value;
    const filters: SearchFilters = { ...state.filters };

    let thisFilter: string[] = filters[filterType] || [];
    thisFilter = thisFilter.filter((cValue) => filterValue !== cValue);

    filters[filterType] = thisFilter;

    if (!filters[filterType].length) {
      // empty filters should just be deleted for comparision reasons.
      delete filters[filterType];
    }

    this.updateState({
      filters,
      page: 0,
    });
  }

  /**
   * Remove all filters of the current search query.
   */
  public deleteAllFilters(): void {
    this.updateState({
      filters: {},
      page: 0,
    });
  }

  /**
   * Sets the search query to the correct index.
   * @param data index name
   */
  public setLibrary(library: Enums.CyborgIndexEnum[], touched: boolean): void {
    this.updateState({
      library: library,
      touched: touched,
      page: 0,
    });
  }

  /**
   * Add the provided index to search state.
   * @param data index name
   */
  public addLibrary(library: Enums.CyborgIndexEnum, touched: boolean): void {
    if (!this.state.value.library.includes(library))
      this.updateState({
        library: [...this.state.value.library, library],
        touched: touched,
        page: 0,
      });
  }

  /**
   * Remove the provided index from search state.
   * @param data index name
   */
  public removeLibrary(library: Enums.CyborgIndexEnum, touched: boolean): void {
    if (this.state.value.library.includes(library))
      this.updateState({
        library: this.state.value.library.filter((index) => index != library),
        touched: touched,
        page: 0,
      });
  }

  /**
   * Sets the touched value of library.
   * @param data index name
   */
  public setLibraryTouched(value: boolean): void {
    this.updateState({
      touched: value,
      page: 0,
    });
  }

  /**
   * Search query page size
   *
   * @param pageSize of pages
   */
  public setPageSize(size: number): void {
    this.updateState({ size });
  }

  /**
   * Search query pagination
   *
   * @param page zero based page number
   */
  public setPage(page: number): void {
    this.updateState({ page });
  }

  /**
   * Search query sort
   * @param sort the sort order and direction
   */
  public setSort(sort: Enums.CyborgSortEnum): void {
    this.updateState({ sort });
  }

  /**
   * Set the search term.
   * @param term search term
   */
  public setTerm(term: string): void {
    this.updateState({
      term: [...this.state.value.term, term],
      page: 0,
    });
  }

  /**
   * Remove the search term.
   * @param term search term to remove
   */
  public removeTerm(term: string): void {
    if (this.state.value.term.includes(term))
      this.updateState({
        term: this.state.value.term.filter((index) => index != term),
        page: 0,
      });
  }

  /**
   * Query the last updated day
   * @param time last updated in days
   */
  public setLastUpdated(time: number): void {
    this.updateState({
      lastUpdated: time,
      page: 0,
    });
  }

  /**
   * Compatible mode restricts the results to only items which are compatible with your system.
   */
  public setCompatible(): void {
    if (
      this.state.value.library.includes(
        Enums.CyborgIndexEnum.CYBORG_USE_CASES,
      ) &&
      this.state.value.touched
    ) {
      this.updateState({
        compatible: true,
        touched: true,
        page: 0,
      });
    } else if (
      this.state.value.library.includes(
        Enums.CyborgIndexEnum.CYBORG_USE_CASES,
      ) &&
      !this.state.value.touched
    ) {
      this.updateState({
        compatible: true,
        library: [Enums.CyborgIndexEnum.CYBORG_USE_CASES],
        touched: true,
        page: 0,
      });
    } else {
      this.updateState({
        compatible: true,
        library: [
          ...this.state.value.library,
          Enums.CyborgIndexEnum.CYBORG_USE_CASES,
        ],
        touched: true,
        page: 0,
      });
    }
  }

  /**
   * Reset compatible mode
   */
  public resetCompatible(): void {
    this.updateState({
      compatible: false,
      page: 0,
    });
  }

  /**
   * Reset the search
   */
  public reset(): void {
    this.updateState(DEFAULT_SEARCH_STATE);
  }

  /**
   * Does the search state have filters
   *
   * @returns are there filters in the search state
   */
  public hasFilters(): boolean {
    const filters = this.state.value.filters;
    for (const key in filters) {
      if (filters[key].length) {
        return true;
      }
    }
    return false;
  }

  /**
   * Return boolean if provided filter is currently active
   */
  public isFilterActive = (
    filterType: string,
    filterValue: string,
  ): boolean => {
    return (this.state.value.filters[filterType] ?? []).includes(filterValue);
  };

  private tagsToFacets(newTags: TagDictionary): SearchResultFacets {
    const tags: TagType[] = [];
    const mitreTags: TagType[] = [];
    const killChains: TagType[] = [];
    const tools: TagType[] = [];
    const platformTypes: TagType[] = [];
    const dataSources: TagType[] = [];
    const goals: TagType[] = [];
    const dependencies: TagType[] = [];

    for (const tag in newTags) {
      if (newTags[tag].length > 0) {
        const tagObj: TagType = {
          type: tag,
          tags: newTags[tag],
        };

        if (
          tag !== 'mitre_technique_ids' &&
          tag !== 'mitre_technique_names' &&
          tag !== 'mitre_tactic_names' &&
          tag !== 'tools' &&
          tag !== 'platform_types' &&
          tag !== 'goals' &&
          tag !== 'dependencies'
        ) {
          tags.push(tagObj);
        }

        if (tag.includes('mitre')) {
          mitreTags.push(tagObj);
        }
        if (tag === 'tools') {
          tools.push(tagObj);
        }
        if (tag === 'platform_types') {
          platformTypes.push(tagObj);
        }
        if (tag === 'data_sources') {
          dataSources.push(tagObj);
        }
        if (tag === 'goals') {
          goals.push(tagObj);
        }
        if (tag === 'dependencies') {
          dependencies.push(tagObj);
        }
      }
    }

    return {
      allTags: newTags,
      tags,
      mitreTags,
      killChains,
      tools,
      platformTypes,
      dataSources,
      goals,
      dependencies,
    };
  }

  /**
   * Update values of existing state, leaving any unprovided values as they were in previous state
   *
   * Cleans any input values to ensure deep equality checks on state pipes are respected
   */
  private updateState(newState: Partial<SearchStateInputs>): void {
    this.internalState.next({
      ...this.state.value,
      ...newState,
      filters: this.cleanFilters(newState.filters ?? this.state.value.filters),
    });
  }

  /**
   * Patch method to clean filters and ensure uniqueness and validity while bound components read and update state in bad fashion
   */
  private cleanFilters(filters: SearchFilters): SearchFilters {
    for (const filterType in filters) {
      filters[filterType] = Array.from(new Set(filters[filterType]));

      if (!filters[filterType].length) {
        // empty filters should just be deleted for comparison reasons.
        delete filters[filterType];
      }
    }
    return filters;
  }

  /**
   * Add any additional values for external state generated from input state values
   */
  private generateExtendedState = (
    state: SearchStateInputs,
  ): SearchStateExtended => {
    return {
      ...state,
      filtersDenormalized: this.denormalizeFilters(state.filters),
    };
  };

  /**
   * Parse filters into format expected by filter pane
   */
  private denormalizeFilters(filters: SearchFilters): DenormalizedFilter[] {
    const denormalized: DenormalizedFilter[] = [];

    for (const filter in filters) {
      if (filters[filter].length) {
        for (const val of filters[filter]) {
          denormalized.push({ type: filter, filter: val });
        }
      }
    }

    return denormalized;
  }
}
